import { withBase } from "./base.ts";
// The storefront a buyer page is for (GET /api/storefront, src/api/storefront.ts publicBrand): its name, logo, colors
// and address. Each buyer page (design, checkout, mockup, client, fix) asks with its own context and fills its
// data-brand slots. The static markup is Bag Carriers, so a page whose fetch fails reads as it always has.

export interface Brand {
  id: string;
  name: string;
  logoUrl: string | null;
  paper: string | null;
  ink: string | null;
  accent: string | null;
  accent2: string | null;
  publicBaseUrl: string;
  // Contact, policies and the pickup place (ruling R24), checked by the server; null when unset. Optional here so a
  // page given an older answer still works.
  contactPhone?: string | null;
  contactEmail?: string | null;
  homeUrl?: string | null;
  termsUrl?: string | null;
  privacyUrl?: string | null;
  refundUrl?: string | null;
  pickupText?: string | null;
  // Type (storefront theme): font families and the Google Fonts stylesheet serving them; optional for older answers.
  headingFont?: string | null;
  labelFont?: string | null;
  fontsUrl?: string | null;
  // The house shop (the static page's own markup): the page keeps today's look and copy for it. Absent in an older
  // cached answer: read as another storefront only when it has its own paper and ink.
  house?: boolean;
}

const FALLBACK_NAME = "Bag Carriers";
// Whether a brand is the house shop's (its own static page, so nothing to switch).
export const isHouse = (b: Pick<Brand, "house" | "paper" | "ink">): boolean => b.house === true || (b.house === undefined && !(b.paper && b.ink));
// The last brand applied on this host, read by brand-early.ts before the first paint so a storefront's page never
// shows Bag Carriers' header while /api/storefront is on its way.
export const BRAND_CACHE_PREFIX = "pressroom.brand:";
const FONT_RE = /^[A-Za-z0-9][A-Za-z0-9 ]{1,39}$/;
const FONTS_LINK_ID = "brand-fonts";
let current: Brand | null = null;

// The storefront's name for the page's own copy ("Send to <name>"); Bag Carriers until a brand is applied.
export function brandName(): string {
  return current?.name ?? FALLBACK_NAME;
}

// Copy that names the storefront once it is known (a brand applied), else the neutral words: a page whose brand fetch
// failed (it may be another storefront's) names no one, never Bag Carriers by default. Bag Carriers' page, once known,
// reads exactly as it did with brandName().
export function named(withName: (name: string) => string, neutral: string): string {
  return current ? withName(current.name) : neutral;
}

// How the buyer reaches the storefront, inside a sentence ("Please <phrase>."): its phone, else its email. Bag Carriers'
// number only on a page known to be Bag Carriers'. Null when the storefront is not known (its fetch failed and nothing
// else named it) or has neither: the copy then leaves the contact out (ruling R24).
export function contactPhrase(): string | null {
  const b = current;
  if (!b) return null;
  if (b.contactPhone) return `call us at ${b.contactPhone}`;
  if (b.contactEmail) return `email us at ${b.contactEmail}`;
  return null;
}

// "<text>, or <contact>." with the storefront's contact, or "<text>." without one.
export function orContact(text: string, joiner = ", or "): string {
  const c = contactPhrase();
  return c ? `${text}${joiner}${c}.` : `${text}.`;
}

export const paymentUnavailable = () => {
  const c = contactPhrase();
  return c ? `Online payment is not available right now. Please ${c}.` : "Online payment is not available right now. Try again in a moment.";
};

// The storefront's own site for a request or a question (homeUrl; the house shop's is its request form page). Null
// while the storefront is not known or has none.
export function homeLink(): { href: string; label: string } | null {
  const href = current?.homeUrl ?? null;
  return href ? { href, label: href.replace(/^https:\/\//, "").replace(/\/$/, "") } : null;
}

// The pickup choice's words: Bag Carriers keeps the page's own (legacy), another storefront says its pickup place,
// or that it is set up by email.
export function pickupLabel(legacy: string, suffix = ""): string {
  if (!current) return `Pickup${suffix}`;
  if (isHouse(current)) return legacy;
  return `${current.pickupText ?? "Pickup at a time and place we set up with you by email"}${suffix}`;
}

// The storefront the page's copy speaks for, without touching the page (applyBrand does both).
export function setBrand(b: Brand): void {
  current = b;
}

// Fills the page's slots: the logo, every name, the title ("<page> | <name>") and the colors. Another storefront's
// page hides what is Bag Carriers' own (data-brand="house": its address, phone, links) and its footer reads
// "© <year> <name>. All rights reserved."
export function applyBrand(b: Brand, doc: Document = document): void {
  setBrand(b);
  const page = doc.title.split(" | ")[0]!.trim();
  doc.title = page ? `${page} | ${b.name}` : b.name;
  fillNames(b.name, doc);
  const nav = doc.querySelector("nav.bar");
  if (nav) nav.setAttribute("aria-label", b.name);
  // Theme first: Bag Carriers' page drops a storefront theme a cached brand may have set (brand-early.ts).
  applyTheme(b, doc);
  const own = !isHouse(b);
  if (!own) {
    restoreHouse(doc);
    return;
  }
  for (const img of doc.querySelectorAll<HTMLImageElement>('img[data-brand="logo"]')) {
    if (b.logoUrl && /^https:\/\//.test(b.logoUrl)) {
      img.dataset.houseSrc ??= img.getAttribute("src") ?? "";
      img.src = b.logoUrl;
    }
  }
  for (const el of doc.querySelectorAll<HTMLElement>('[data-brand="house"]')) el.hidden = true;
  for (const a of doc.querySelectorAll<HTMLAnchorElement>('a[data-brand="home"]')) {
    a.dataset.houseHref ??= a.getAttribute("href") ?? "";
    a.href = https(b.homeUrl) ?? b.publicBaseUrl;
  }
  // The storefront's own contact and policies in the footer, each only when it has one.
  const footer = doc.querySelector<HTMLElement>('[data-brand="footer"]');
  if (footer) {
    footer.querySelectorAll('[data-brand="own"]').forEach((el) => el.remove());
    const line = (...kids: (Node | string)[]) => {
      const p = doc.createElement("p");
      p.dataset.brand = "own";
      kids.forEach((k, i) => {
        if (i > 0) p.append(" \u00b7 ");
        p.append(k);
      });
      return p;
    };
    const a = (href: string, text: string) => {
      const el = doc.createElement("a");
      el.href = href;
      el.textContent = text;
      return el;
    };
    const contact = [
      ...(b.contactPhone ? [a(`tel:${b.contactPhone.replace(/[^0-9+]/g, "")}`, b.contactPhone)] : []),
      ...(b.contactEmail ? [a(`mailto:${b.contactEmail}`, b.contactEmail)] : []),
    ];
    if (contact.length > 0) footer.append(line(...contact));
    const policies = ([["Terms", b.termsUrl], ["Privacy", b.privacyUrl], ["Refund Policy", b.refundUrl]] as const)
      .flatMap(([label, url]) => (https(url) ? [a(https(url)!, label)] : []));
    if (policies.length > 0) footer.append(line(...policies));
  }
}

// Bag Carriers' page as served: the static logo and home link back, its own links shown, another storefront's footer
// lines gone. Needed when a cached brand (brand-early.ts) dressed the page as a storefront before Bag Carriers answered.
function restoreHouse(doc: Document): void {
  for (const img of doc.querySelectorAll<HTMLImageElement>('img[data-brand="logo"]')) {
    if (img.dataset.houseSrc !== undefined) {
      img.setAttribute("src", img.dataset.houseSrc);
      delete img.dataset.houseSrc;
    }
  }
  for (const el of doc.querySelectorAll<HTMLElement>('[data-brand="house"]')) el.hidden = false;
  for (const a of doc.querySelectorAll<HTMLAnchorElement>('a[data-brand="home"]')) {
    if (a.dataset.houseHref !== undefined) {
      a.setAttribute("href", a.dataset.houseHref);
      delete a.dataset.houseHref;
    }
  }
  doc.querySelectorAll('[data-brand="footer"] [data-brand="own"]').forEach((el) => el.remove());
}

// Every name slot takes the storefront's name. The footer's "© <year> <name>. All rights reserved." keeps one full
// stop when the name ends with one ("Lost Fly Co." never reads "Lost Fly Co..").
export function fillNames(name: string, doc: Document = document): void {
  for (const el of doc.querySelectorAll<HTMLElement>('[data-brand="name"]')) {
    if (el.textContent !== name) el.textContent = name;
    const next = el.nextSibling;
    if (next?.nodeType !== 3 || !next.textContent) continue;
    if (name.endsWith(".") && next.textContent.startsWith(".")) {
      next.textContent = next.textContent.slice(1);
      el.dataset.stop = "removed";
    } else if (!name.endsWith(".") && el.dataset.stop === "removed") {
      next.textContent = `.${next.textContent}`;
      delete el.dataset.stop;
    }
  }
}

// The storefront's colors, type and the theme switch (public/site.css html[data-shop="own"]: paper header and hero,
// the logo at its own shape, the heading and label fonts). Bag Carriers' own page keeps today's look: no switch, no
// variables set. Safe to run before the body exists (brand-early.ts), so it touches only <html> and <head>.
export function applyTheme(b: Brand, doc: Document = document): void {
  const html = doc.documentElement;
  const root = html.style;
  const own = !isHouse(b);
  if (own) html.dataset.shop = "own";
  else delete html.dataset.shop;
  const set = (name: string, v: string | null | undefined) => {
    if (v && /^#[0-9a-f]{3,8}$/i.test(v)) root.setProperty(name, v);
  };
  if (own) {
    set("--paper", b.paper);
    set("--ink", b.ink);
    set("--accent", b.accent);
    set("--accent2", b.accent2);
    // The page's own accents (site.css) follow the storefront's.
    set("--rust", b.accent);
    set("--rust-bright", b.accent2);
    set("--leather-btn", b.accent);
    set("--leather-btn-hover", b.accent2);
  }
  const heading = own && b.headingFont && FONT_RE.test(b.headingFont) ? b.headingFont : null;
  const label = own && b.labelFont && FONT_RE.test(b.labelFont) ? b.labelFont : null;
  if (heading) root.setProperty("--heading-font", `"${heading}", Georgia, serif`);
  else root.removeProperty("--heading-font");
  if (label) root.setProperty("--label-font", `"${label}", "DM Mono", monospace`);
  else root.removeProperty("--label-font");
  const fonts = own ? fontsUrl(b.fontsUrl) : null;
  const link = doc.getElementById(FONTS_LINK_ID) as HTMLLinkElement | null;
  if (fonts) {
    if (link?.getAttribute("href") === fonts) return;
    link?.remove();
    const el = doc.createElement("link");
    el.id = FONTS_LINK_ID;
    el.rel = "stylesheet";
    el.href = fonts;
    doc.head.append(el);
  } else {
    link?.remove();
  }
}

// Only a Google Fonts stylesheet is ever linked, whatever the brand row says.
function fontsUrl(v: string | null | undefined): string | null {
  const u = https(v);
  if (!u) return null;
  try { return new URL(u).host === "fonts.googleapis.com" ? u : null; } catch { return null; }
}

// Remembers the brand for brand-early.ts (per host; a failed write, private mode say, is nothing).
export function cacheBrand(b: Brand, storage: Pick<Storage, "setItem"> | null = storageOrNull(), hostname = hostOrEmpty()): void {
  try { storage?.setItem(BRAND_CACHE_PREFIX + hostname, JSON.stringify(b)); } catch { /* storage refused: next load fetches as before */ }
}
function storageOrNull(): Storage | null {
  try { return typeof localStorage === "undefined" ? null : localStorage; } catch { return null; }
}
function hostOrEmpty(): string {
  return typeof location === "undefined" ? "" : location.hostname;
}

const https = (v: string | null | undefined): string | null => (v && /^https:\/\//.test(v) ? v : null);

// GET /api/storefront?<query> (for=design, mockup=<token>) and apply it. Null, with the page left as it is, when the
// fetch fails: the page still works as Bag Carriers'.
export async function loadBrand(query: string): Promise<Brand | null> {
  try {
    const res = await fetch(withBase(`/api/storefront?${query}`), {});
    if (!res.ok) return null;
    const b = (await res.json()) as Brand;
    if (typeof b?.id !== "string" || typeof b.name !== "string") return null;
    applyBrand(b);
    cacheBrand(b);
    return b;
  } catch {
    return null;
  }
}
